import type { FrozenFile, FrozenSubmission, PublicEvent } from '../../shared/types';
import type { SignatureInput } from '../lib/pdf';
import { loadParticipantPdf } from '../lib/pdfForms';
import { preparationService } from '../pdf/preparation/service';
import type { PreparationJob } from '../pdf/preparation/jobs';
import { documentArtifactRequest } from './pdfPreview';
import { bytesSha256 } from './snapshot';
import type { RegState } from './state';

export interface RebuildContext {
  ev: PublicEvent; state: RegState; snapshot: FrozenSubmission; template: Uint8Array | null;
  participantSig: SignatureInput | null; parentSig: SignatureInput | null;
}
/**
 * After a reload, prepare one frozen PDF again from the saved snapshot. Generation is
 * deterministic, so the result is accepted only if its length and SHA-256 equal the frozen
 * file's; anything else returns null and nothing different is ever sent under this attempt.
 */
export async function rebuildFrozenPdf(file: FrozenFile, context: RebuildContext, signal: AbortSignal): Promise<Uint8Array | null> {
  const { ev, state, snapshot } = context;
  let job: PreparationJob;
  if (file.kind === 'standard') {
    const parent = snapshot.signedBy === 'participant+parent';
    if (!context.template || !context.participantSig || parent && !context.parentSig) return null;
    job = { kind: 'standard', options: { template: context.template, event: ev, answers: state.builtin, signedOn: snapshot.signedOn, flatten: true, participantSignature: context.participantSig, ...(parent ? { parentSignature: context.parentSig! } : {}) } };
  } else if (file.kind === 'document') {
    const document = ev.documentForms?.find(d => d.id === file.documentId), completion = state.pdfProgress?.documents[file.documentId!]?.completion;
    if (!document || !completion) return null;
    const request = documentArtifactRequest(ev, document, state.builtin, state.custom, completion, false, snapshot.signedOn);
    if (request.mode !== 'final') return null;
    const source = await loadParticipantPdf(ev, document, signal);
    job = { kind: 'document', input: { ...request.context, template: source.bytes, mode: 'final' } };
  } else return null;
  const result = await preparationService().prepare(job, signal);
  if (!result.ok || result.bytes.length !== file.bytes || await bytesSha256(result.bytes) !== file.sha256) return null;
  return result.bytes;
}
